import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import Reveal, { Eyebrow, fadeUp } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { SERVICE_ICONS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

export default function Home() {
  const { t } = useLang();
  const services = t.services.items;
  const stats = t.stats;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative navy-texture text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBjb25zdWx0aW5nJTIwbWVldGluZ3xlbnwwfHx8fDE3ODM1NDUzNjJ8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#1B3A5B]/70" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Eyebrow dark>{t.company.eyebrow}</Eyebrow>
            </motion.div>
            <motion.h1
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeUp}
              className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance"
            >
              {t.company.tagline}
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeUp}
              className="mt-7 text-lg text-slate-200 leading-relaxed max-w-2xl"
            >
              {t.company.valueProp}
            </motion.p>
            <motion.div
              initial="hidden"
              animate="visible"
              custom={3}
              variants={fadeUp}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="inline-flex items-center gap-2 bg-[#C9A15A] text-white hover:bg-[#B38C48] transition-colors duration-300 px-8 py-3.5 text-sm font-medium rounded-sm"
              >
                {t.home.heroServicesBtn}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/a-propos"
                data-testid="hero-about-button"
                className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-[#1B3A5B] transition-colors duration-300 px-8 py-3.5 text-sm font-medium rounded-sm"
              >
                {t.home.heroAboutBtn}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-slate-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i} className="text-center lg:text-left">
                <div className="font-serif-display text-4xl sm:text-5xl font-bold text-[#C9A15A]">
                  {s.value}
                </div>
                <div className="mt-3 text-xs sm:text-sm uppercase tracking-widest text-slate-500 leading-snug">
                  {s.label}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO / WHO WE ARE */}
      <section className="bg-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>{t.home.whoEyebrow}</Eyebrow>
                <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1B3A5B] text-balance">
                  {t.home.whoTitle}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={1}>
                <p className="text-slate-600 leading-relaxed text-base md:text-lg">
                  {t.company.intro}
                </p>
                <p className="mt-6 text-slate-600 leading-relaxed">
                  {t.company.approach}
                </p>
                <Link
                  to="/a-propos"
                  data-testid="intro-about-link"
                  className="mt-8 inline-flex items-center gap-2 text-[#1B3A5B] font-medium hover:text-[#C9A15A] transition-colors"
                >
                  {t.home.introLink}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE OVERVIEW */}
      <section className="bg-[#F8FAFC] border-y border-slate-200" data-testid="expertise-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <Eyebrow>{t.services.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1B3A5B] max-w-2xl text-balance">
              {t.home.expertiseTitle}
            </h2>
            <p className="mt-5 text-slate-600 max-w-2xl">{t.services.subtitle}</p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.slice(0, 4).map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={i}>
                  <div className="group h-full bg-white border border-slate-200 rounded-sm p-7 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-b-4 border-b-transparent hover:border-b-[#C9A15A]">
                    <div className="h-12 w-12 rounded-full bg-[#C9A15A]/12 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-[#C9A15A]" />
                    </div>
                    <h3 className="mt-5 font-serif-display text-lg font-bold text-[#1B3A5B] leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {s.description}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={1}>
            <div className="mt-10">
              <Link
                to="/services"
                data-testid="overview-all-services-link"
                className="inline-flex items-center gap-2 border border-[#1B3A5B] text-[#1B3A5B] hover:bg-[#1B3A5B] hover:text-white transition-colors duration-300 px-8 py-3.5 text-sm font-medium rounded-sm"
              >
                {t.home.allServicesLink}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow>{t.reasons.eyebrow}</Eyebrow>
                <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1B3A5B] text-balance">
                  {t.reasons.title}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="grid gap-5 sm:grid-cols-2">
                {t.reasons.items.map((r, i) => (
                  <Reveal key={i} delay={i}>
                    <div className="h-full bg-[#F8FAFC] border border-slate-200 rounded-sm p-6">
                      <div className="flex items-center gap-3">
                        <span className="h-8 w-8 rounded-full bg-[#1B3A5B] flex items-center justify-center shrink-0">
                          <Check className="h-4 w-4 text-[#C9A15A]" />
                        </span>
                        <h3 className="font-semibold text-[#1B3A5B]">{r.title}</h3>
                      </div>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                        {r.description}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
